import { useAuth } from './useAuth';
import { useQuery } from '@tanstack/react-query';
import { billing } from '../lib/api';
import { useConfig } from './useConfig';
import { resolveFeatureAccess, planMeetsRequirement, type FeatureId, type PlanTier } from '../config/featureGates';

export type { FeatureId } from '../config/featureGates';

export function usePremiumAccess() {
  const { user } = useAuth();
  const { data: config } = useConfig();
  const billingStatus = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => billing.getStatus(),
    staleTime: 30_000,
    retry: false,
    enabled: !!user,
  });

  const isEnterprise = (user?.plan === 'enterprise') || billingStatus.data?.plan === 'enterprise';
  const billingSaysPro = billingStatus.data?.plan === 'pro'
    || billingStatus.data?.subscriptionStatus === 'active'
    || billingStatus.data?.subscriptionStatus === 'trialing';
  const isPaidPro = isEnterprise || (user?.plan === 'pro') || billingSaysPro;
  const hasEnterpriseAccess = isEnterprise;
  const tempUnlocked = user?.featureFlags?.premiumTempUnlocked ?? false;
  const billingGateEnabled = user?.featureFlags?.billingGateEnabled ?? true;

  const effectivePlan: PlanTier = isEnterprise ? 'enterprise' : isPaidPro ? 'pro' : 'free';
  const betaUnlocked = !!user && config?.beta === true && effectivePlan === 'free';
  const hasAccess = isPaidPro || tempUnlocked || betaUnlocked;
  const showUpgradePrompt = !hasAccess && billingGateEnabled;

  const featureAccess = resolveFeatureAccess(config?.featureAccess);

  const canAccess = (featureId: FeatureId): boolean => {
    if (tempUnlocked || betaUnlocked) return true;
    return planMeetsRequirement(effectivePlan, featureAccess[featureId]);
  };

  return {
    isPaidPro, isEnterprise, hasEnterpriseAccess, tempUnlocked, betaUnlocked,
    hasAccess, showUpgradePrompt,
    canAccess, effectivePlan,
  };
}
